/**
 * Shared Individual Time Entry PDF layout — web jsPDF and NativeScript
 * `@triniwiz/nativescript-pdf` both consume these numbers so the two
 * reports cannot drift (ADO #30817).
 *
 * Page is landscape US Letter in points (792 × 612), matching the web
 * `jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })`.
 */

export const ITE_PDF_PAGE = {
  width: 792,
  height: 612,
  margin: 36,
} as const;

export const ITE_PDF_LOGO = {
  width: 108,
  height: 36,
  /** Drawn top-right so it does not collide with the left-aligned title. */
  top: 8,
} as const;

const HOUR_COL = 46;
const PD_COL = 36;

export interface IndividualTimeEntryPdfColumns {
  wo: number;
  st: number;
  ot: number;
  dt: number;
  vac: number;
  sick: number;
  hol: number;
  trv: number;
  pd: number;
  notes: number;
  excNotes: number;
}

export interface IndividualTimeEntryPdfColumnX extends IndividualTimeEntryPdfColumns {
  tableRight: number;
}

/**
 * Hour columns stay fixed. When Per Diem is hidden (MSI / Co 47) its width
 * is given to Notes + Exc Notes so the table does not leave a hole.
 */
export function buildIndividualTimeEntryPdfColumns(
  showPd: boolean,
  pageWidth: number = ITE_PDF_PAGE.width,
  margin: number = ITE_PDF_PAGE.margin
): {
  colW: IndividualTimeEntryPdfColumns;
  col: IndividualTimeEntryPdfColumnX;
  tableRight: number;
} {
  const pd = showPd ? PD_COL : 0;
  const hrCols = pageWidth - margin * 2 - 190;
  const noteColW = Math.floor((hrCols - HOUR_COL * 7 - pd) / 2);
  const colW: IndividualTimeEntryPdfColumns = {
    wo: 190,
    st: HOUR_COL,
    ot: HOUR_COL,
    dt: HOUR_COL,
    vac: HOUR_COL,
    sick: HOUR_COL,
    hol: HOUR_COL,
    trv: HOUR_COL,
    pd,
    notes: noteColW,
    excNotes: noteColW,
  };

  let x = margin;
  const col = {
    wo: x,
    st: (x += colW.wo),
    ot: (x += colW.st),
    dt: (x += colW.ot),
    vac: (x += colW.dt),
    sick: (x += colW.vac),
    hol: (x += colW.sick),
    trv: (x += colW.hol),
    pd: (x += colW.trv),
    notes: (x += colW.pd),
    excNotes: (x += colW.notes),
    tableRight: pageWidth - margin,
  };

  return { colW, col, tableRight: col.tableRight };
}

export function formatIndividualTimeEntryJobHeading(
  name: string | null | undefined,
  vpJobId: string | null | undefined
): string {
  const trimmedName = (name || '').trim();
  const trimmedId = (vpJobId || '').trim();
  if (trimmedName && trimmedId) {
    return `${trimmedName}  (${trimmedId})`;
  }
  return trimmedName || trimmedId || '';
}

export function individualTimeEntryShiftName(
  selectedShift: number | null | undefined
): string {
  return selectedShift === 2 ? 'Night' : 'Day';
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** NativeScript `toLocaleDateString` ignores options and prints "Wed Aug 26 2026". */
export function formatIndividualTimeEntryPdfDate(
  date: Date | null | undefined
): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '—';
  }
  return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function formatIndividualTimeEntryHoursCell(
  hours: number | null | undefined
): string {
  const value = Number(hours ?? 0);
  return value > 0 ? String(value) : '';
}

/**
 * Word-wrap for PDF note cells so a long note cannot smash neighboring
 * hour columns. Hard-breaks tokens longer than `maxLineLength`.
 */
export function wrapIndividualTimeEntryPdfNote(
  text: string | null | undefined,
  maxLineLength: number
): string {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length || maxLineLength <= 0) {
    return '';
  }

  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length <= maxLineLength) {
      currentLine = nextLine;
      continue;
    }
    if (currentLine) {
      lines.push(currentLine);
    }
    if (word.length <= maxLineLength) {
      currentLine = word;
      continue;
    }
    let remaining = word;
    while (remaining.length > maxLineLength) {
      lines.push(remaining.slice(0, maxLineLength));
      remaining = remaining.slice(maxLineLength);
    }
    currentLine = remaining;
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join('\n');
}

export function noteWrapLengthForColumnWidth(columnWidthPt: number): number {
  // ~7pt per glyph at 8pt Helvetica; keep a small pad so wrapped lines
  // stay inside the Notes / Exc Notes cell.
  return Math.max(8, Math.floor((columnWidthPt - 6) / 4.2));
}
